/**
 * LE JOUR FÉRIÉ COMME ÉVÉNEMENT DE CALENDRIER — module PUR (RQ, 05/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Tu peux ajouter les jours fériés dans mon planning. Écrire le jour férié
 * comme un événement dans le calendrier. Pareil pour le calendrier du
 * laboratoire. »
 *
 * ── Ce qui manquait, et ce qui ne marchait pas ─────────────────────────────
 *
 * « Mon planning » n'affichait AUCUN férié : la grille montrait un 25 juillet
 * comme un mercredi ordinaire.
 *
 * Le « Calendrier du laboratoire », lui, en affichait déjà — mais presque
 * personne ne les voyait, pour deux défauts cumulés constatés le 05/10/2026 :
 *   · `GET /ged/rh/feries` exigeait le rôle `rh` (2 comptes) alors que l'écran
 *     s'ouvre avec la section « Planning » ; le `.catch(() => [])` du magasin
 *     rendait le refus muet ;
 *   · l'écran appelait `loadRh()`, qui ne remplit PAS `rhFeries` (c'est
 *     `loadRhGestion`). La liste restait donc vide même pour qui avait le droit.
 * Un calendrier vide ne se lit pas comme un droit refusé : il se lit comme un
 * calendrier sans fériés. Les deux sont corrigés ; ce module tient la mise en
 * forme, une seule fois pour les deux écrans.
 *
 * ── ⚠️ UN FÉRIÉ N'EST PAS UNE FERMETURE ───────────────────────────────────
 *
 * Le laboratoire TRAVAILLE les jours fériés — c'est le constat qui a corrigé
 * le calendrier d'ouverture des postes, où le défaut intuitif (« fériés
 * fermés ») était faux. L'événement se pose donc À CÔTÉ des affectations,
 * jamais à leur place, et son vocabulaire ne dit jamais « fermé ».
 *
 * `chome` ne veut pas dire « le laboratoire ferme » : il dit que le jour est
 * férié au sens de la loi (et le régime de paie suit). Un férié déclaré NON
 * chômé est un jour ouvré que le calendrier signale quand même.
 *
 * ⚠️ MODULE SANS AUCUN IMPORT (feuille) : il est compilé et éprouvé par
 * `npm test` (préfixe `front/`), et une dépendance y amènerait tout le graphe
 * de l'application.
 */

export interface FerieVue {
  date: string;
  libelle: string;
  /** Férié au sens de la loi. ⚠️ Ne veut PAS dire que le laboratoire ferme. */
  chome?: boolean;
}

/**
 * Les trois teintes de l'événement « férié ».
 *
 * ⚠️ VOLONTAIREMENT DISTINCTES DE LA PALETTE DES CONVENTIONS
 * (`@/lib/conventions`) : dans la même case de calendrier, un férié et un
 * passage en clinique ne sont pas de même nature — l'un est subi, l'autre
 * organisé. Reprendre une couleur de convention les ferait lire comme deux
 * événements du même registre.
 *
 * L'ambre est celle que le planning emploie déjà pour ce qui sort de
 * l'ordinaire (garde, dérogation) : le férié s'y rattache sans inventer un
 * sixième code couleur.
 */
export const COULEUR_FERIE = {
  fond: '#FCF2E1',
  texte: '#7A4F00',
  bord: '#F0CF8B',
} as const;

/**
 * Le libellé COURT, pour une case de calendrier large de sept jours.
 *
 * Les libellés réels atteignent 90 caractères — « Fête de l'indépendance
 * (coïncide avec l'Aïd el Fitr J1 — régime Indépendance retenu) ». Tronqué par
 * le CSS, il ne reste que « Fête de l'indép… » ; ce qui suit la parenthèse est
 * une note de gestion, pas le nom de la fête.
 *
 * ⚠️ RIEN N'EST PERDU : les écrans mettent le libellé ENTIER en infobulle et en
 * `aria-label`. Raccourcir l'affichage n'est permis que parce que le texte
 * complet reste atteignable — sans quoi ce serait effacer une information.
 */
export function libelleCourtFerie(libelle: string): string {
  const net = String(libelle || '').trim();
  if (!net) return 'Jour férié';
  const sansParenthese = net.replace(/\s*\([^)]*\)\s*$/, '').trim();
  return sansParenthese || net;
}

/** Le texte complet pour l'infobulle : le libellé entier, et la mention
 *  « non chômé » quand c'en est un — c'est l'exception, elle se dit. */
export function titreFerie(f: FerieVue): string {
  const base = `Jour férié : ${String(f.libelle || '').trim() || 'non précisé'}`;
  return f.chome === false ? `${base} — déclaré non chômé` : base;
}

/** date → le férié de ce jour. Une seule entrée par date : `rh-jour-ferie.date`
 *  est UNIQUE en base, deux fériés le même jour n'existent pas. */
export function parDateFerie(liste: FerieVue[] | null | undefined): Map<string, FerieVue> {
  const m = new Map<string, FerieVue>();
  for (const f of liste || []) {
    const d = String(f?.date || '').slice(0, 10);
    if (d && !m.has(d)) m.set(d, { date: d, libelle: String(f.libelle || ''), chome: f.chome !== false });
  }
  return m;
}

/** Les fériés d'une fenêtre, triés — pour un bandeau de période.
 *  Bornes INCLUSES, comme partout ailleurs dans le planning. */
export function feriesEntre(liste: FerieVue[] | null | undefined, du: string, au: string): FerieVue[] {
  return [...parDateFerie(liste).values()]
    .filter((f) => f.date >= du && f.date <= au)
    .sort((a, b) => a.date.localeCompare(b.date));
}
